// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// palette.ts — every colour that carries meaning, in one place so it can be MEASURED.
//
// A colour literal inline in the renderer is a colour nobody can hold to a contrast ratio. A sibling
// game in this fleet drew its walls at 1.14:1 against the board — on screen, in the right cells, at the
// right size, and completely invisible — and the screenshot looked atmospheric, so every geometry gate
// passed it. tests/contrast.test.ts holds everything below to >= 3:1 (WCAG 2.1 SC 1.4.11, the floor for
// a non-text graphic) against EVERY floor-ramp step it can be drawn on.
//
// FLOOR LIGHTNESS IS THE ONLY THING ENCODING LIGHT, and light is the input to effRange — the number the
// whole "read a shape, never a formula" claim rests on. So arcs are drawn as an outline plus a single
// NON-ACCUMULATING fill: a cell inside three arcs is tinted exactly once. Stacking translucent fills
// destroyed the ramp in testing, which meant the one visual channel that tells you how far somebody can
// see was being overwritten by the drawing of how far they can see.
//
// Colours are Okabe-Ito, which is the standard eight-colour set chosen to stay distinguishable under
// protanopia, deuteranopia and tritanopia. Floor TYPE is texture rather than hue for the same reason:
// the map you read for space is the same picture you read for volume.

/** The floor ramp. Index by light level: 0 gloom, 1 dim, 2 normal, 3 bright. */
export const FLOOR_RAMP = ['#0b0f15', '#151c27', '#232c3a', '#34414f'] as const;

export const PALETTE = {
  /** Everything outside the building. */
  void: '#05070a',
  // Walls were '#4b5769' and measured 1.31:1 against a bright floor — structure you cannot see, on a
  // screenshot that looks perfectly atmospheric. The contrast test caught it before a pixel was drawn.
  wall: '#93a1b5',
  /**
   * Walls are FILLED with this and EDGED with `wall`. Only the edge carries meaning, so only the edge
   * is held to a contrast ratio; a solid bright fill made the walls louder than the rooms.
   */
  wallFill: '#1a212c',
  /** A door, by state. Locked is deliberately the warmest, because it is the one that costs you. */
  doorOpen: '#7e8ea3',
  doorShut: '#aab7c8',
  doorLocked: '#E69F00',

  intruder: '#F0E442',
  intruderCreep: '#C2B63A',

  /** Minders, by what they are doing. Also the heat-band ramp, so one colour language covers both. */
  minderCalm: '#56B4E9',
  minderLooking: '#E69F00',
  minderChasing: '#E4763A',
  /**
   * The custodian is OFF the threat ramp on purpose. He is not a threat, he is a man carrying your
   * money away, and a player has to read that difference in one glance rather than work it out.
   */
  custodian: '#CC79A7',
  lens: '#7FC7E8',

  loot: '#12BC8C',
  chute: '#3FB99B',
  exit: '#FFFFFF',
  duct: '#8CB4D2',
  breaker: '#E8C86A',
  mark: '#E8EDF4',

  /** Route preview, by the worst thing that happens along it. */
  routeClear: '#12BC8C',
  routeExposed: '#E69F00',
  routeSeen: '#E4763A',

  hint: '#F5A3C7',
  arc: '#8FD3F4',
  arcProjected: '#B7C4D6',

  text: '#E8EDF4',
  textDim: '#9FB0C4',
  panel: '#141a24',
  panelEdge: '#2b3545',
} as const;

/** The four heat bands, as one ramp — the same language as the minder states. */
export const BAND_COLOUR = ['#56B4E9', '#E69F00', '#E4763A', '#F0424E'] as const;
export const BAND_NAME = ['CALM', 'RESTLESS', 'SWEEP', 'LOCKDOWN'] as const;

/** Every surface a meaningful colour can be drawn on. The contrast test walks this list. */
export const SURFACES: readonly string[] = [...FLOOR_RAMP, PALETTE.panel];

const srgb = (v: number): number => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);

export function luminance(hex: string): number {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.replace(/(.)/g, '$1$1') : h, 16);
  return (
    0.2126 * srgb(((n >> 16) & 255) / 255) +
    0.7152 * srgb(((n >> 8) & 255) / 255) +
    0.0722 * srgb((n & 255) / 255)
  );
}

export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
